import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { statsApi, type TimeseriesEntry } from '@entities/stats';
import { FullscreenSpinner } from '@shared/ui';
import { KpiCard } from './kpi-card';
import { QuickActions } from './quick-actions';
import { RecentAudit } from './recent-audit';

const KPI_ACTIONS = [
  'iam.UserRegistered',
  'iam.UserVerified',
  'catalog.ProductPublished',
  'order.OrderCreated',
  'rfq.QuoteAccepted',
] as const;

function last30Days(): { from: string; to: string } {
  const to = new Date();
  to.setUTCHours(0, 0, 0, 0);
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - 29);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function DashboardPage() {
  const { t } = useTranslation();

  const stats = useQuery({
    queryKey: ['stats'],
    queryFn: () => statsApi.get(),
  });

  const range = useMemo(last30Days, []);
  const timeseries = useQuery({
    queryKey: ['stats', 'timeseries', range, KPI_ACTIONS],
    queryFn: () =>
      statsApi.timeseries({
        from: range.from,
        to: range.to,
        groupBy: 'DAY',
        actions: [...KPI_ACTIONS],
      }),
  });

  if (stats.isLoading) return <FullscreenSpinner />;

  const counts = stats.data?.counts;
  const series = new Map<string, TimeseriesEntry>(
    timeseries.data?.series.map((s) => [s.action, s]) ?? [],
  );
  const pointsFor = (action: string) => series.get(action)?.points;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">{t('dashboard.title')}</h1>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard
          label={t('dashboard.kpis.usersRegistered')}
          value={counts?.['iam.UserRegistered']}
          points={pointsFor('iam.UserRegistered')}
        />
        <KpiCard
          label={t('dashboard.kpis.manufacturersVerified')}
          value={counts?.['iam.UserVerified']}
          points={pointsFor('iam.UserVerified')}
        />
        <KpiCard
          label={t('dashboard.kpis.productsPublished')}
          value={counts?.['catalog.ProductPublished']}
          points={pointsFor('catalog.ProductPublished')}
        />
        <KpiCard
          label={t('dashboard.kpis.ordersPlaced')}
          value={counts?.['order.OrderCreated']}
          points={pointsFor('order.OrderCreated')}
        />
        <KpiCard
          label={t('dashboard.kpis.quotesAccepted')}
          value={counts?.['rfq.QuoteAccepted']}
          points={pointsFor('rfq.QuoteAccepted')}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <QuickActions counts={counts} />
        </div>
        <div className="lg:col-span-2">
          <RecentAudit />
        </div>
      </div>
    </div>
  );
}
